import { memo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Globe } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

import type { ResearchSource, TurnResearch } from "../../domain/types";
import { Pill } from "../common/Pill";
import { cn } from "../common/cn";

interface ResearchSubcardProps {
  research: TurnResearch;
  defaultOpen?: boolean;
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    // Fallback: strip protocol if present and take the first path-less chunk.
    return url.replace(/^https?:\/\//, "").split("/")[0] ?? url;
  }
}

function safeHref(url: string): string {
  // Defensive: only pass through http(s) and protocol-relative URLs to <a>.
  // Anything else (javascript:, data:, etc.) is rendered as a noop link.
  if (/^https?:\/\//i.test(url) || url.startsWith("//")) return url;
  return `https://${url.replace(/^\/+/, "")}`;
}

function faviconFor(url: string): string {
  const host = hostFromUrl(url);
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
}

function fmtPublished(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const SourceRow = memo(function SourceRow({ source }: { source: ResearchSource }) {
  const published = fmtPublished(source.published);
  return (
    <li className="flex items-start gap-3 rounded-md px-2 py-2 transition hover:bg-white/[0.04]">
      <img
        src={faviconFor(source.url)}
        alt=""
        loading="lazy"
        decoding="async"
        width={20}
        height={20}
        className="mt-0.5 h-5 w-5 flex-none rounded-sm bg-white/10 ring-1 ring-white/10"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <a
            href={safeHref(source.url)}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-strong underline-offset-4 hover:underline focus-ring rounded-sm"
          >
            <span className="line-clamp-2">{source.title}</span>
            <ExternalLink className="h-3 w-3 flex-none text-fg-subtle" aria-hidden />
          </a>
          <Pill tone="info" className="text-[10px] uppercase tracking-wider">
            {source.name}
          </Pill>
          {published ? (
            <span className="font-mono text-[10px] tabular-nums text-fg-subtle">
              {published}
            </span>
          ) : null}
        </div>
        {source.summary ? (
          <p className="mt-0.5 line-clamp-3 text-xs leading-snug text-fg-muted">
            {source.summary}
          </p>
        ) : null}
      </div>
    </li>
  );
});

export const ResearchSubcard = memo(function ResearchSubcard({
  research,
  defaultOpen = true,
}: ResearchSubcardProps) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const reduceMotion = useReducedMotion();

  const total = research.sources.length;
  const elapsed = research.elapsedMs;
  const cacheLabel = research.cacheHit ? " · cache" : "";

  return (
    <section
      className={cn(
        "glass relative flex min-w-0 flex-col gap-1.5 overflow-hidden rounded-lg border border-white/[0.06] px-3 py-2.5",
        "bg-[radial-gradient(circle_at_25%_-20%,hsl(220_70%_70%/0.08),transparent_60%)]",
      )}
      aria-label={`Web kaynakları (${total})`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring flex items-center justify-between gap-3 rounded-md text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-medium text-fg-muted">
          <Globe className="h-3.5 w-3.5 text-sky-300" strokeWidth={1.5} aria-hidden />
          <span>
            Web kaynakları ({total})
            <span className="ml-2 font-mono tabular-nums text-fg-subtle">
              {elapsed} ms{cacheLabel}
            </span>
          </span>
        </span>
        <span className="text-fg-subtle">
          {open ? (
            <ChevronDown className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
          )}
        </span>
      </button>

      {research.query ? (
        <div className="min-w-0 text-[11px] leading-snug text-fg-subtle">
          <span className="text-fg-muted">Sorgu:</span>{" "}
          <span
            className="font-mono text-fg-muted break-all line-clamp-2"
            title={research.query}
          >
            {research.query}
          </span>
        </div>
      ) : null}

      <AnimatePresence initial={false}>
        {open ? (
          <motion.ul
            key="sources"
            initial={reduceMotion ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="mt-1 flex flex-col gap-0.5 overflow-hidden"
          >
            {research.sources.map((s, i) => (
              <SourceRow key={`${s.url}-${i}`} source={s} />
            ))}
            {research.sources.length === 0 ? (
              <li className="px-2 py-2 text-xs italic text-fg-subtle">
                Bu sorgu için açık web kaynağı bulunamadı. Uzman değerlendirmesini
                yüklenen belgeler ve sektör birikimine dayandırıyor.
              </li>
            ) : null}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </section>
  );
});
